import React from 'react';

import { ApolloProvider } from 'react-apollo'
import { ApolloClient } from 'apollo-client'
import { createHttpLink } from 'apollo-link-http'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import { ToastProvider } from 'react-toast-notifications'

import Home from './Home';
import PublicHome from './PublicHome';
import DashboardList from './DashboardList';
import Login from './auth/Login';
import Logout from './auth/Logout';

import '../styles/App.css';
import { history as routerHistory } from '../history';
import { Profile } from './admin/Profile';
import { Organization } from './admin/org/Organization';
import Data from './admin/data/Data';
import { ROLE_ADMIN, ROLE_SUPER_ADMIN }  from './auth/Auth';
import RoleProtectedRoute from './auth/RoleProtectedRoute';
import { FieldManagerApp } from '../apps/fields';
import Register from './auth/Register';

const customFetch = (uri: any, options: any) => {
  const token = localStorage.getItem('syncarto_jwt');
  options.headers.Authorization = token ? `Bearer ${token}` : "";
  return fetch(uri, options);
};

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URI,
  fetch: customFetch,
});

const client = new ApolloClient({
  link: httpLink,
  cache: new InMemoryCache(),
});

export default class App extends React.Component<any, any> {
  constructor(props:any) {
    super(props);
    this.state = {
      currentUser: null,
      authenticated: false,
      isAuthenticating: true,
      userOrgs: null,
    };

    this.refreshCurrentUser = this.refreshCurrentUser.bind(this);
    this.refreshAllCredentials = this.refreshAllCredentials.bind(this);
  }

  private refreshIntervalId: any;

  async refreshCurrentUser(orgId?: string) {
    const { auth } = this.props;
    console.log("Refreshing current user");
    return auth.getUserOrgs(client).then((userOrgs:any) => {
      console.log("Retrieved user details from DB");
      let userOrg;
      if (orgId) {
        userOrg = userOrgs.filter((org: any) => org.orgId === orgId)[0];
      } else {
        userOrg = userOrgs[0];
      }
      localStorage.setItem('syncarto_org_id', userOrg.orgId);
      this.setState({
        currentUser: userOrg,
        isAuthenticating: false,
        userOrgs,
      });
    }).catch((err:any) => {
      console.error("Could not auth: ", err);
      this.setState({
        isAuthenticating: false,
      });
    });
  }

  async refreshAllCredentials(orgId?: string) {
    const { auth } = this.props;
    console.log("User is logged in; refreshing credentials");

    // avoid letting id token expire via cognito refresh token.
    // this needs to finish before dashboard list or any of the components
    // start making requests that depend on the id token not being expired.
    await auth.refreshToken(orgId);

    // Now that we have refreshed jwt, this should work.
    // Get the current user data and assign to state
    await this.refreshCurrentUser(orgId);
  }

  async componentDidMount() {
    const { auth } = this.props;

    // If the user is already logged in
    if (auth.isAuthenticated()) {
      const orgId = localStorage.getItem('syncarto_org_id');
      await this.refreshAllCredentials(orgId ? orgId : undefined);
    } else {
      // User is not logged in
      console.log("User is not logged in");
      this.setState({
        isAuthenticating: false,
      });
    }

    this.refreshIntervalId = setInterval(
      () => {
        const orgId = localStorage.getItem('syncarto_org_id');
        auth.refreshToken(orgId ? orgId : undefined);
      },
      5*60*1000); // every 5 minutes
  }

  componentWillUnmount() {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
    }
  }

  render() {
    const { auth, history } = this.props;
    const { currentUser, isAuthenticating } = this.state;

    return (
      <Router history={routerHistory}>
        <ApolloProvider client={client}>
          <ToastProvider placement="bottom-center">
            <Switch>
              <Route exact path="/register">
                <Register />
              </Route>
              <Route path="/login">
                <Login auth={auth} history={history} userOrgs={this.state.userOrgs} refreshCurrentUser={this.refreshCurrentUser} refreshAllCredentials={this.refreshAllCredentials} />
              </Route>
              <Route path="/logout">
                <Logout auth={auth} history={history} currentUser={currentUser} />
              </Route>
              <Route path="/switchOrgs">
                {/* placeholder to facilitate "refreshing" the dashboard list on org change (using jwt for new org) */}
                <Redirect to={process.env.REACT_APP_FIELD_MGR_ROOT_URL} push={true} />
              </Route>
              <Route path="/switchOrgsFromDashboard">
                {/* placeholder to facilitate "refreshing" the dashboard list on org change (using jwt for new org) */}
                <Redirect to="/" push={true} />
              </Route>
              <Route path="/switchOrgsFromAdmin">
                {/* placeholder to facilitate "refreshing" the dashboard list on org change (using jwt for new org) */}
                <Redirect to="/org/profile" push={true} />
              </Route>
              <Route exact path="/">
                {isAuthenticating ?
                  // avoids getAllOrgs request with invalid jwt on component mount
                  null
                  : <DashboardList isLoading={isAuthenticating} currentUser={currentUser} userOrgs={this.state.userOrgs} refreshAllCredentials={this.refreshAllCredentials} />
                }
              </Route>
              <Route path="/profile">  
                { isAuthenticating ? null : <Profile currentUser={currentUser} userOrgs={this.state.userOrgs} refreshAllCredentials={this.refreshAllCredentials} client={client} /> }
              </Route>
              {currentUser &&
              (
                <Route path={`${process.env.REACT_APP_FIELD_MGR_ROOT_URL}`}>
                  <FieldManagerApp currentUser={currentUser} userOrgs={this.state.userOrgs} refreshAllCredentials={this.refreshAllCredentials} />
                </Route>
              )}

              {currentUser &&
              (
                <RoleProtectedRoute path="/org" component={Organization} 
                  role={currentUser.roleId} 
                  roles={[ROLE_ADMIN, ROLE_SUPER_ADMIN]} 
                  currentUser={currentUser} 
                  userOrgs={this.state.userOrgs} 
                  refreshAllCredentials={this.refreshAllCredentials} 
                  client={client} />
              )}
              {currentUser &&
              (
                <RoleProtectedRoute path="/data" component={Data} 
                  role={currentUser.roleId} 
                  roles={[ROLE_ADMIN, ROLE_SUPER_ADMIN]} 
                  currentUser={currentUser} 
                  userOrgs={this.state.userOrgs} 
                  refreshAllCredentials={this.refreshAllCredentials} 
                  client={client} />
              )}
              {/*<Route path="/data">  
                { isAuthenticating ? null : <Data currentUser={currentUser} userOrgs={this.state.userOrgs} refreshAllCredentials={this.refreshAllCredentials} />}
              </Route>*/}
              <Route exact path="/public/:id/:session?">
                  <PublicHome auth={auth} currentUser={currentUser} userOrgs={this.state.userOrgs} refreshAllCredentials={this.refreshAllCredentials} />
              </Route>
              <Route exact path="/:id/:session?">
                {isAuthenticating ?
                  null
                  : <Home isLoading={isAuthenticating} currentUser={currentUser} userOrgs={this.state.userOrgs} refreshAllCredentials={this.refreshAllCredentials} />
                }
              </Route>
            </Switch>
          </ToastProvider>
        </ApolloProvider>
      </Router>
    );
  }
}
